import React, { useMemo, useState } from 'react';
import { ResponsiveBar, BarDatum } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AcceptedCommodity, TopLevelResult } from '@/lib/api/risk/profiles';
import { formatNumberToDisplay, safeParseDecimal } from '@/lib/api/common';
import { Uom } from '@/lib/api/commodity';
import { cn } from '@/lib/utils';
import { Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SummaryCardProps {
  data: TopLevelResult['summaries'] | undefined;
  isLoading: boolean;
  customers: { id: string; long_name: string }[];
}

const CommonNivoFillDefs = [
  {
    id: 'dots',
    type: 'patternDots',
    background: 'inherit',
    color: 'rgba(255, 255, 255, 0.1)',
    size: 4,
    padding: 1,
    stagger: true
  },
  {
    id: 'lines',
    type: 'patternLines',
    background: 'inherit',
    color: 'rgba(255, 255, 255, 0.2)',
    rotation: -45,
    lineWidth: 6,
    spacing: 10
  }
];

const NivoProductionFills = [
  {
    match: {
      id: "Projected Production"
    },
    id: 'dots'
  },
  {
    match: {
      id: "Actual Production"
    },
    id: 'lines'
  },
];

const NivoUomGraphFills = [
  {
    match: {
      id: Uom.Bushel
    },
    id: 'dots'
  },
  {
    match: {
      id: Uom.Lbs
    },
    id: 'lines'
  },
];

const NivoCommodityGraphFills = [
  {
    match: {
      id: AcceptedCommodity.Canola
    },
    id: 'dots'
  },
  {
    match: {
      id: AcceptedCommodity.Corn
    },
    id: 'dots'
  },
  {
    match: {
      id: AcceptedCommodity.Soybeans
    },
    id: 'lines'
  },
  {
    match: {
      id: AcceptedCommodity.MnSpringHrsw
    },
    id: 'lines'
  },
  {
    match: {
      id: AcceptedCommodity.KcWinterWheatHrw
    },
    id: 'lines'
  },
  {
    match: {
      id: AcceptedCommodity.ChicagoWinterWheatSrw
    },
    id: 'dots'
  },
  {
    match: {
      id: AcceptedCommodity.Oats
    },
    id: 'lines'
  },
  {
    match: {
      id: AcceptedCommodity.Sorghum
    },
    id: 'dots'
  },
  {
    match: {
      id: AcceptedCommodity.SoybeanMeal
    },
    id: 'dots'
  },
  {
    match: {
      id: AcceptedCommodity.Sunflowers
    },
    id: 'dots'
  },
];

export const SummaryCard: React.FC<SummaryCardProps> = ({ data, isLoading, customers }) => {
  const [isVertical, setIsVertical] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <p>Loading summaries...</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent>
          <p>No summary data available</p>
        </CardContent>
      </Card>
    );
  }

  const toggleVertical = () => {
    setIsVertical(!isVertical);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Production Summaries</CardTitle>
          <div>
            <Button variant={"outline"} size="sm" onClick={toggleVertical}>
              {isVertical ? "Dense" : "Vertical"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="per_crop">
          <TabsList className="mb-4">
            <TabsTrigger value="per_crop">Per Crop</TabsTrigger>
            <TabsTrigger value="per_customer">Per Customer</TabsTrigger>
          </TabsList>

          <TabsContent value="per_crop">
            <h3 className="text-lg font-medium mb-4">Crop Breakdown</h3>
            <PerCropBreakdown commoditySummaries={data.commodity_summaries} isVertical={isVertical} />
          </TabsContent>

          <TabsContent value="per_customer">
            <h3 className="text-lg font-medium mb-4">Customer Breakdown</h3>
            <PerCustomerBreakdown customerSummaries={data.customer_summaries} isVertical={isVertical} customers={customers} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

function PerCustomerBreakdown({ customers, customerSummaries, isVertical }: { customerSummaries: TopLevelResult['summaries']['customer_summaries']; isVertical: boolean; customers: { id: string; long_name: string }[] }) {
  const { customer_options } = useMemo(() => {
    const customer_ids = Object.keys(customerSummaries);
    return {
      customer_options: customer_ids.map((id) => {
        return ({ value: id, label: customers.find((customer) => customer.id === id)?.long_name || "" })
      }).sort((a, b) => a.label.localeCompare(b.label)),
    };
  }, [customers, customerSummaries]);

  return (
    <div className="">
      {customer_options.map((option) => {
        const commoditySummaries = (customerSummaries[option.value] as any).commodity_summaries;
        return (
          <div key={`per_customer_breakdown_customer_${option.value}`}>
            <p className="text-2xl font-bold">{option.label}</p>
            <PerCropBreakdown commoditySummaries={commoditySummaries} isVertical={isVertical} />
          </div>
        );
      })}
    </div>
  );
}

function PerCropBreakdown({ commoditySummaries, isVertical }: { commoditySummaries: TopLevelResult['summaries']['commodity_summaries']; isVertical: boolean }) {
  const commodityNames = Object.keys(commoditySummaries).sort()
  return (
    <div className={cn(isVertical ? "" : "grid grid-cols-2")}>
      {commodityNames.map(commodity => {
        const cropYears = Object.keys(commoditySummaries[commodity].by_year).sort();
        return (
          <div key={`PerCropBreakdown_${commodity}`}>
            <p className="font-semibold text-xl">{commodity}</p>
            <div className="h-[400px]">
              <CommodityCropYearsChart
                data={cropYears.map((cropYear) => {
                  const entry = commoditySummaries[commodity].by_year[cropYear];
                  return {
                    crop_year: cropYear,
                    projected: safeParseDecimal({})(entry.total_projected_production).toNumber(),
                    actual: safeParseDecimal({})(entry.total_actual_production).toNumber(),
                  };
                })}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const UomSummaries = ({ uomSummaries }: { uomSummaries: TopLevelResult['summaries']['uom_summaries'] }) => {
  return (
    <div>
      <div className="h-[400px]">
        <TopLevelProductionSummaryChart
          data={Object.entries(uomSummaries).map(([uom, uomData]) => {
            return {
              uom: uom,
              projected: safeParseDecimal({})(uomData.total_projected_production).toNumber(),
              actual: safeParseDecimal({})(uomData.total_actual_production).toNumber(),
            };
          })}
        />
      </div>
    </div>
  );
};

// New component for year breakdown
const YearBreakdown = ({ uomSummaries }: { uomSummaries: TopLevelResult['summaries']['uom_summaries'] }) => {
  const [selectedUom, setSelectedUom] = useState<string | null>(
    Object.keys(uomSummaries)[0] || null
  );

  if (!selectedUom) return <p>No UOM data available</p>;

  const uomOptions = Object.keys(uomSummaries);

  return (
    <div>
      <div className="mb-4">
        <select
          value={selectedUom}
          onChange={(e) => setSelectedUom(e.target.value)}
          className="p-2 border rounded"
        >
          {uomOptions.map(uom => (
            <option key={uom} value={uom}>{uom}</option>
          ))}
        </select>
      </div>

      <div className="h-[400px]">
        <YearTrendChart uomData={uomSummaries[selectedUom]} />
      </div>
    </div>
  );
};

// New component for year-commodity breakdown
const YearCommodityBreakdown = ({ uomSummaries }: { uomSummaries: TopLevelResult['summaries']['uom_summaries'] }) => {
  const [selectedUom, setSelectedUom] = useState<string | null>(
    Object.keys(uomSummaries)[0] || null
  );
  const [selectedYear, setSelectedYear] = useState<string | null>(null);

  const uomOptions = Object.keys(uomSummaries);
  const yearOptions = selectedUom ? Object.keys(uomSummaries[selectedUom].by_year) : [];

  // Set initial selected year if not already set
  React.useEffect(() => {
    if (yearOptions.length > 0 && !selectedYear) {
      setSelectedYear(yearOptions[0]);
    }
  }, [selectedUom, yearOptions]);

  if (!selectedUom) return <p>No UOM data available</p>;
  if (!selectedYear) return <p>Loading year data...</p>;

  return (
    <div>
      <div className="flex gap-4 mb-4">
        <select
          value={selectedUom}
          onChange={(e) => {
            setSelectedUom(e.target.value);
            setSelectedYear(null); // Reset year when UOM changes
          }}
          className="p-2 border rounded"
        >
          {uomOptions.map(uom => (
            <option key={uom} value={uom}>{uom}</option>
          ))}
        </select>

        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          className="p-2 border rounded"
        >
          {yearOptions.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-8">
        <div>
          <h4 className="text-md font-medium mb-2">Year by Year Breakdown</h4>
          <div className="h-[400px]">
            <YearCommodityBarChart
              uomData={uomSummaries[selectedUom]}
            />
          </div>
        </div>

        <div>
          <h4 className="text-md font-medium mb-2">Commodity Distribution for {selectedYear}</h4>
          <div className="h-[400px]">
            <CommodityPieChart
              yearData={uomSummaries[selectedUom].by_year[selectedYear]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface ProductionData {
  uom: string;
  ['Projected Production']?: number;
  ['Actual Production']?: number;
}

interface CropProductionData {
  crop_year: string;
  ['Projected Production']?: number;
  ['Actual Production']?: number;
}

function CommodityCropYearsChart(params: { data: { projected: number; actual: number; crop_year: string }[] }) {
  const data: CropProductionData[] = params.data.map(item => ({
    crop_year: item.crop_year,
    'Projected Production': item.projected,
    'Actual Production': item.actual,
  }));

  return (
    <ResponsiveBar
      groupMode='grouped'
      layout='horizontal'
      theme={{ text: { fontSize: 12 } }}
      tooltip={(item) => {
        const formattedValue = formatNumberToDisplay(item.value, { emptyFormat: '--' })
        return (
          <div
            style={{
              background: 'white',
              padding: '9px 12px',
              border: '1px solid #ccc',
            }}
          >
            {`${formattedValue}`}
          </div>
        )
      }}
      data={data as any}
      keys={['Projected Production', 'Actual Production']}
      indexBy="crop_year"
      margin={{ top: 50, right: 210, bottom: 50, left: 60 }}
      padding={0.5}
      valueScale={{ type: 'linear' }}
      colors={{ scheme: 'paired' }}
      defs={CommonNivoFillDefs}
      fill={NivoProductionFills}
      borderColor={{
        from: 'color',
        modifiers: [
          [
            'darker',
            4
          ]
        ]
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Crop Year',
        legendPosition: 'middle',
        legendOffset: -55
      }}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Volume',
        legendPosition: 'middle',
        legendOffset: 40,
        format: value =>
          Intl.NumberFormat('en-US', {
            notation: 'compact',
            compactDisplay: 'short'
          }).format(value)
      }}
      animate={true}
      label={d => {
        const formattedValue = formatNumberToDisplay(d.value, { emptyFormat: "--" });
        return `${formattedValue}`;
      }}
      legends={[
        {
          dataFrom: 'keys',
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 120,
          translateY: 0,
          itemsSpacing: 2,
          itemWidth: 100,
          itemHeight: 20,
          itemDirection: 'left-to-right',
          itemOpacity: 0.85,
          symbolSize: 20,
          effects: [
            {
              on: 'hover',
              style: {
                itemOpacity: 1
              }
            }
          ]
        }
      ]}
      labelSkipWidth={12}
      labelSkipHeight={12}
      labelTextColor="white"
      role="application"
    />
  );
}

function TopLevelProductionSummaryChart(params: { data: { projected: number; actual: number; uom: string }[] }) {
  const data: ProductionData[] = params.data.map(item => ({
    uom: item.uom,
    'Projected Production': item.projected,
    'Actual Production': item.actual,
  }));

  return (
    <ResponsiveBar
      groupMode='grouped'
      layout='horizontal'
      theme={{ text: { fontSize: 12 } }}
      tooltip={(item) => {
        const formattedValue = formatNumberToDisplay(item.value, { emptyFormat: '--' })
        return (
          <div
            style={{
              background: 'white',
              padding: '9px 12px',
              border: '1px solid #ccc',
            }}
          >
            {`${formattedValue} ${item.indexValue}`}
          </div>
        )
      }}
      data={data as any}
      keys={['Projected Production', 'Actual Production']}
      indexBy="uom"
      margin={{ top: 50, right: 210, bottom: 50, left: 60 }}
      padding={0.5}
      valueScale={{ type: 'linear' }}
      colors={{ scheme: 'paired' }}
      defs={CommonNivoFillDefs}
      fill={NivoProductionFills}
      borderColor={{
        from: 'color',
        modifiers: [
          [
            'darker',
            4
          ]
        ]
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Production Type',
        legendPosition: 'middle',
        legendOffset: -50
      }}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Volume',
        legendPosition: 'middle',
        legendOffset: 40,
        format: value =>
          Intl.NumberFormat('en-US', {
            notation: 'compact',
            compactDisplay: 'short'
          }).format(value)
      }}
      animate={true}
      label={d => {
        const formattedValue = formatNumberToDisplay(d.value, { emptyFormat: "--" });
        return `${formattedValue} ${d.indexValue}`;
      }}
      legends={[
        {
          dataFrom: 'keys',
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 120,
          translateY: 0,
          itemsSpacing: 2,
          itemWidth: 100,
          itemHeight: 20,
          itemDirection: 'left-to-right',
          itemOpacity: 0.85,
          symbolSize: 20,
          effects: [
            {
              on: 'hover',
              style: {
                itemOpacity: 1
              }
            }
          ]
        }
      ]}
      labelSkipWidth={12}
      labelSkipHeight={12}
      labelTextColor="white"
      role="application"
    />
  );
}

// New chart component for year trend visualization
function YearTrendChart({ uomData }: { uomData: TopLevelResult['summaries']['uom_summaries'][string] }) {
  // Transform data for line chart
  const lineData = [
    {
      id: 'Projected Production',
      data: Object.entries(uomData.by_year).map(([year, yearData]) => ({
        x: year,
        y: safeParseDecimal({})(yearData.total_projected_production).toNumber()
      })).sort((a, b) => parseInt(a.x as string) - parseInt(b.x as string))
    },
    {
      id: 'Actual Production',
      data: Object.entries(uomData.by_year).map(([year, yearData]) => ({
        x: year,
        y: safeParseDecimal({})(yearData.total_actual_production).toNumber()
      })).sort((a, b) => parseInt(a.x as string) - parseInt(b.x as string))
    }
  ];

  return (
    <ResponsiveLine
      data={lineData}
      margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
      xScale={{ type: 'point' }}
      yScale={{
        type: 'linear',
        min: 'auto',
        max: 'auto',
        stacked: false,
        reverse: false
      }}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Year',
        legendOffset: 36,
        legendPosition: 'middle'
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Production Volume',
        legendOffset: -40,
        legendPosition: 'middle',
        format: value =>
          Intl.NumberFormat('en-US', {
            notation: 'compact',
            compactDisplay: 'short'
          }).format(value as number)
      }}
      colors={{ scheme: 'paired' }}
      pointSize={10}
      pointColor={{ theme: 'background' }}
      pointBorderWidth={2}
      pointBorderColor={{ from: 'serieColor' }}
      pointLabelYOffset={-12}
      useMesh={true}
      legends={[
        {
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 100,
          translateY: 0,
          itemsSpacing: 0,
          itemDirection: 'left-to-right',
          itemWidth: 80,
          itemHeight: 20,
          itemOpacity: 0.75,
          symbolSize: 12,
          symbolShape: 'circle',
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1
              }
            }
          ]
        }
      ]}
      tooltip={({ point }) => (
        <div
          style={{
            background: 'white',
            padding: '9px 12px',
            border: '1px solid #ccc',
          }}
        >
          <strong>{point.seriesId} ({point.data.x}): </strong>
          {Intl.NumberFormat('en-US').format(point.data.y as number)}
        </div>
      )}
    />
  );
}

// New chart for year-commodity breakdown
function YearCommodityBarChart({ uomData }: { uomData: TopLevelResult['summaries']['uom_summaries'][string] }) {
  // Transform data for stacked bar chart
  const data: any[] = [];

  // Get all unique commodities across all years
  const allCommodities = new Set<string>();
  Object.values(uomData.by_year).forEach(yearData => {
    Object.keys(yearData.by_commodity).forEach(commodity => {
      allCommodities.add(commodity);
    });
  });

  // Create data for each year with all commodities
  Object.entries(uomData.by_year).forEach(([year, yearData]) => {
    const yearEntry: any = { year };

    // Add actual production for each commodity
    allCommodities.forEach(commodity => {
      if (yearData.by_commodity[commodity]) {
        yearEntry[commodity] = safeParseDecimal({})(
          yearData.by_commodity[commodity].total_actual_production
        ).toNumber();
      } else {
        yearEntry[commodity] = 0;
      }
    });

    data.push(yearEntry);
  });

  // Sort data by year
  data.sort((a, b) => parseInt(a.year) - parseInt(b.year));

  // Convert Set to Array for Nivo
  const commoditiesArray = Array.from(allCommodities);

  return (
    <ResponsiveBar
      data={data}
      keys={commoditiesArray}
      indexBy="year"
      margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
      padding={0.3}
      groupMode="stacked"
      valueScale={{ type: 'linear' }}
      colors={{ scheme: 'category10' }}
      borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Year',
        legendPosition: 'middle',
        legendOffset: 40
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Production Volume',
        legendPosition: 'middle',
        legendOffset: -50,
        format: value =>
          Intl.NumberFormat('en-US', {
            notation: 'compact',
            compactDisplay: 'short'
          }).format(value)
      }}
      labelSkipWidth={12}
      labelSkipHeight={12}
      legends={[
        {
          dataFrom: 'keys',
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 120,
          translateY: 0,
          itemsSpacing: 2,
          itemWidth: 100,
          itemHeight: 20,
          itemDirection: 'left-to-right',
          itemOpacity: 0.85,
          symbolSize: 20,
          effects: [
            {
              on: 'hover',
              style: {
                itemOpacity: 1
              }
            }
          ]
        }
      ]}
      animate={true}
      label={d => {
        const formattedValue = formatNumberToDisplay(d.value, { emptyFormat: "--" });
        return `${formattedValue}`;
      }}
    />
  );
}

// New chart for commodity distribution within a specific year
function CommodityPieChart({ yearData }: { yearData: TopLevelResult['summaries']['uom_summaries'][string]['by_year'][string] }) {
  // Transform data for pie chart
  const pieData = Object.entries(yearData.by_commodity).map(([commodity, data]) => ({
    id: commodity,
    label: commodity,
    value: safeParseDecimal({})(data.total_actual_production).toNumber()
  })).filter(item => item.value > 0);

  return (
    <ResponsivePie
      data={pieData}
      margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
      innerRadius={0.5}
      padAngle={0.7}
      cornerRadius={3}
      activeOuterRadiusOffset={8}
      colors={{ scheme: 'category10' }}
      borderWidth={1}
      borderColor={{ from: 'color', modifiers: [['darker', 0.8]] }}
      arcLinkLabelsSkipAngle={10}
      arcLinkLabelsThickness={2}
      arcLinkLabelsColor={{ from: 'color' }}
      arcLabelsSkipAngle={10}
      arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 8]] }}
      arcLinkLabelsTextColor={{ from: 'color', modifiers: [['darker', 8]] }}
      legends={[
        {
          anchor: 'right',
          direction: 'column',
          justify: false,
          translateX: 0,
          translateY: 56,
          itemsSpacing: 0,
          itemWidth: 160,
          itemHeight: 25,
          itemTextColor: '#999',
          itemDirection: 'left-to-right',
          itemOpacity: 1,
          symbolSize: 18,
          symbolShape: 'circle'
        }
      ]}
      fill={NivoCommodityGraphFills}
      defs={CommonNivoFillDefs}
      arcLabel={d => {
        const formattedValue = formatNumberToDisplay(d.value, { emptyFormat: "--" });
        return `${formattedValue}`;
      }}
      tooltip={(item) => {
        const formattedValue = formatNumberToDisplay(item.datum.value, { emptyFormat: '--' })
        return (
          <div
            style={{
              background: 'white',
              padding: '9px 12px',
              border: '1px solid #ccc',
            }}
          >
            {`${item.datum.id}: ${formattedValue}`}
          </div>
        )
      }}
    />
  );
}

export default SummaryCard;
